import * as React from 'react';
import { useTranslation } from 'react-i18next';
import MapBoxMap, { FullscreenControl, Marker, NavigationControl, Popup } from 'react-map-gl';

import { Link } from '@tanstack/react-router';

import { Card } from '@/components/ui/card';
import { env } from '@/constants/env';
import { EstateImage } from '@/features/estates-property/components/estate-image';
import type { Projects } from '@/features/projects/api/projects';
import { cn } from '@/lib/utils';
import { getFinalPrice } from '@/utils/getFinalPrice';

import type { Estates } from '../api/estates';

type EstateMapData = Pick<Estates[number], 'id' | 'name' | 'status' | 'thumbnail' | 'createdAt'> &
  Partial<Pick<Estates[number], 'creator' | 'estateDetails' | 'estateAreasRooms'>> & {
    address?: {
      address?: string | null;
      postalCode?: string | null;
      city?: string | null;
      lat?: number | null;
      long?: number | null;
    } | null;
    prices?: Projects[number]['prices'] | Estates[number]['prices'] | null;
    areaRooms?: Projects[number]['areaRooms'] | null;
  };

const MAP_BOX_TOKEN = env.MAP_BOX_API_TOKEN;

interface EstatesMapProps {
  data?: EstateMapData[];
  isPending?: boolean;
}

// Default latitude and longitude for the map center if no estates have valid coordinates
const DEFAULT_LATLONG = {
  latitude: 47.5162,
  longitude: 14.5501,
};

const isValidCoordinate = (value: unknown, min: number, max: number): value is number => {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
};

const getValidLatLong = (address: EstateMapData['address']) => {
  if (!isValidCoordinate(address?.lat, -90, 90) || !isValidCoordinate(address?.long, -180, 180)) {
    return null;
  }

  return {
    latitude: address.lat,
    longitude: address.long,
  };
};

export const EstatesMap: React.FC<EstatesMapProps> = ({ data = [], isPending = false }) => {
  const { t } = useTranslation();
  const [openedEstate, setOpenedEstate] = React.useState<EstateMapData | null>(null);

  const defaultLatLong = React.useMemo(() => {
    const defaultEstateLatLong = data
      .map((estate) => getValidLatLong(estate.address))
      .find((latLong) => latLong !== null);

    return defaultEstateLatLong ?? DEFAULT_LATLONG;
  }, [data]);

  const openedEstateLatLong = getValidLatLong(openedEstate?.address);

  return (
    <Card className="mb-6 flex-1 p-0">
      <div
        className={cn(
          'size-full',
          '[&_.mapboxgl-ctrl-group]:top-[50%] [&_.mapboxgl-ctrl-group]:flex [&_.mapboxgl-ctrl-group]:flex-col [&_.mapboxgl-ctrl-group]:gap-2 [&_.mapboxgl-ctrl-group]:bg-transparent [&_.mapboxgl-ctrl-group]:shadow-none',
          '[&_.mapboxgl-ctrl-shrink]:size-8 [&_.mapboxgl-ctrl-shrink]:!rounded-sm [&_.mapboxgl-ctrl-shrink]:bg-primary-light',
          '[&_.mapboxgl-ctrl-fullscreen]:size-8 [&_.mapboxgl-ctrl-fullscreen]:!rounded-sm [&_.mapboxgl-ctrl-fullscreen]:bg-primary-light',
          '[&_.mapboxgl-ctrl-zoom-in]:size-8 [&_.mapboxgl-ctrl-zoom-in]:!rounded-sm [&_.mapboxgl-ctrl-zoom-in]:bg-primary-light',
          '[&_.mapboxgl-ctrl-zoom-out]:size-8 [&_.mapboxgl-ctrl-zoom-out]:!rounded-sm [&_.mapboxgl-ctrl-zoom-out]:bg-primary-light',
          '[&_.mapboxgl-ctrl-bottom-right]:hidden [&_.mapboxgl-ctrl-compass]:hidden [&_.mapboxgl-ctrl-scale]:hidden [&_a.mapboxgl-ctrl-logo]:hidden'
        )}
      >
        <MapBoxMap
          initialViewState={{
            zoom: 14,
            ...defaultLatLong,
          }}
          mapStyle="mapbox://styles/mapbox/light-v9"
          mapboxAccessToken={MAP_BOX_TOKEN}
        >
          <FullscreenControl position="top-left" />
          <NavigationControl position="top-left" />
          {!isPending && data.length === 0 ? (
            <div className="absolute inset-x-4 top-4 z-10 rounded-sm bg-white px-4 py-3 text-center text-sm text-slate-500 shadow-sm">
              {t('notMatchingResults')}
            </div>
          ) : null}
          {data.map((estate) => {
            const latLong = getValidLatLong(estate.address);

            return latLong ? (
              <Marker
                key={estate.id}
                latitude={latLong.latitude}
                longitude={latLong.longitude}
                anchor="bottom"
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  setOpenedEstate(estate);
                }}
              >
                <Icon className="cursor-pointer" />
              </Marker>
            ) : null;
          })}

          {openedEstate && openedEstateLatLong ? (
            <Popup
              latitude={openedEstateLatLong.latitude}
              longitude={openedEstateLatLong.longitude}
              anchor="bottom"
              onClose={() => setOpenedEstate(null)}
              maxWidth="190px"
            >
              <EstatePopupCard estate={openedEstate} />
            </Popup>
          ) : null}
        </MapBoxMap>
      </div>
    </Card>
  );
};

const Icon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="29"
    height="47"
    viewBox="0 0 29 47"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M14.5 0C6.48929 0 0 6.57203 0 14.6849C0 29.3698 14.5 47 14.5 47C14.5 47 29 29.3802 29 14.6849C29 6.57203 22.5107 0 14.5 0ZM14.5 23.6782C9.59422 23.6782 5.61991 19.6532 5.61991 14.6849C5.61991 9.71655 9.59422 5.69157 14.5 5.69157C19.4058 5.69157 23.3801 9.71655 23.3801 14.6849C23.3801 19.6532 19.4058 23.6782 14.5 23.6782ZM8.88009 14.6849C8.88009 17.8294 11.3951 20.3764 14.5 20.3764C17.6049 20.3764 20.1199 17.8294 20.1199 14.6849C20.1199 11.5404 17.6049 8.99331 14.5 8.99331C11.3951 8.99331 8.88009 11.5404 8.88009 14.6849Z"
      fill="#07B1BE"
    />
  </svg>
);

const EstatePopupCard: React.FC<{ estate: EstateMapData }> = ({
  estate: { id, thumbnail, estateDetails, ...estate },
}) => {
  const { t } = useTranslation();

  const livingArea = estate.estateAreasRooms?.livingArea;
  const roomsCount = estate.estateAreasRooms?.totalNumberOfRooms;
  const price = getFinalPrice(estate);

  return (
    <Link to="/estates/$estateId" params={{ estateId: id }}>
      <Card className="w-[170px] rounded-sm">
        <div className="relative">
          <EstateImage {...(thumbnail && { thumbnailId: thumbnail.mediaId })} />
        </div>
        <div className="mt-1 space-y-1.5 p-1.5">
          <p className="line-clamp-2 flex-1 text-xs font-medium leading-tight">{estate.name}</p>
          <div className="mb-auto flex gap-1 text-[10px] leading-tight">
            <span className="truncate text-text-secondary">{t('external-id')}:</span>
            {estateDetails?.externalId ? (
              <span className="truncate">{estateDetails?.externalId}</span>
            ) : (
              '----'
            )}
          </div>

          <div className="flex w-full items-start justify-between gap-1 text-[9px] leading-tight">
            <div className="mb-auto flex flex-col">
              <span className="truncate text-text-secondary">{t('living area')}:</span>
              {livingArea ? (
                <span className="truncate">
                  {livingArea} m<sup>2</sup>
                </span>
              ) : (
                '----'
              )}
            </div>
            <div className="mb-auto flex flex-col">
              <span className="truncate text-text-secondary">{t('rooms')}:</span>
              {roomsCount ? <span className="truncate">{roomsCount}</span> : '----'}
            </div>
            <div className="mb-auto flex flex-col">
              <span className="truncate text-text-secondary">{t('price')}:</span>
              {price ? <span className="truncate">{price}</span> : '----'}
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
};
